import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLanguage } from '../contexts/LanguageContext';
import ConsultaScheduler from './ConsultaScheduler';

// Antes esta pagina tenia un formulario de 3 pasos que SIMULABA agendar
// una consulta (un setTimeout fingiendo exito, sin llamar a ningun backend,
// sin avisarle a Augusto). Se reemplazo por el calendario real de Augusto
// en Cal.com.
//
// 17 sep 2026 (Augusto: "¿alguna manera de no depender de Cal.com?" tras
// notar que el embed seguia en español con la app en ingles): Cal.com sale
// del todo. El titulo y la descripcion que se veian en español eran texto
// del evento en SU cuenta de Cal.com, no de este codigo, asi que no habia
// forma de traducirlos desde aca. Ahora la agenda es propia
// (ConsultaScheduler.js + /api/v1/consultations en el backend), siguiendo
// el mismo motor que ACS: horario de atencion editable, grilla cada 30
// min, hora de Lima explicita, y cada solicitud la confirma Augusto a
// mano -- que era justo lo que el ya tenia configurado en Cal.com con
// "requiere confirmacion".
//
// Orden de la pagina (a pedido de Augusto): toda la informacion de la
// consultoria -- que incluye, FAQ -- va ANTES del calendario, para que el
// visitante entienda que esta reservando antes de ver los horarios.

const ConsultantPage = () => {
  const theme = useTheme();
  const { language } = useLanguage();

  const includes = language === 'es'
    ? [
        'Sesión personalizada de 60 minutos',
        'Diagnóstico de tu situación financiera actual',
        'Plan de acción personalizado',
        'Seguimiento por email después de la sesión',
      ]
    : [
        'Personalized 60-minute session',
        'Diagnosis of your current financial situation',
        'Personalized action plan',
        'Email follow-up after the session',
      ];

  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            {language === 'es' ? 'Agenda una Consulta' : 'Schedule a Consultation'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, maxWidth: 800, mx: 'auto' }}>
            {language === 'es'
              ? 'Sesión personalizada de 60 minutos con un especialista para diagnosticar tu situación financiera y armar un plan de acción concreto. Cuesta $45 por sesión — ve el detalle en la página de Precios.'
              : "Personalized 60-minute session with a specialist to diagnose your financial situation and build a concrete action plan. It costs $45 per session — see the details on the Pricing page."}
          </Typography>
        </Box>

        <Paper elevation={1} sx={{ p: { xs: 3, md: 4 }, mb: 5 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {language === 'es' ? 'Qué incluye' : "What's included"}
          </Typography>
          <List dense>
            {includes.map((item, idx) => (
              <ListItem key={idx} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {language === 'es' ? '¿Por qué necesito esperar la confirmación?' : 'Why do I need to wait for confirmation?'}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {language === 'es'
                  ? 'El especialista aprueba cada solicitud a mano antes de que quede en firme, para asegurarse de que el horario elegido realmente funciona. Te llega un correo apenas la cita queda confirmada.'
                  : "The specialist approves each request by hand before it's finalized, to make sure the chosen time actually works. You'll get an email as soon as the booking is confirmed."}
              </Typography>

              <Typography variant="h6" gutterBottom>
                {language === 'es' ? '¿Cuánto dura la sesión?' : 'How long is the session?'}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {language === 'es'
                  ? 'La consulta dura 60 minutos, con toda la agenda pensada para dejarte con un plan de acción claro al terminar.'
                  : "The consultation is 60 minutes, with the whole agenda built to leave you with a clear action plan by the end."}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {language === 'es' ? '¿Cómo se paga?' : 'How do I pay?'}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {language === 'es'
                  ? 'El pago se coordina por separado en la página de Precios (Mercado Pago o PayPal); reservar aquí solo aparta tu horario.'
                  : 'Payment is handled separately on the Pricing page (Mercado Pago or PayPal); booking here just reserves your time slot.'}
              </Typography>

              <Typography variant="h6" gutterBottom>
                {language === 'es' ? '¿Qué debo preparar?' : 'What should I prepare?'}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {language === 'es'
                  ? 'Para aprovechar al máximo la sesión, ten a mano un resumen de tus objetivos financieros, tus estados financieros actuales y cualquier pregunta específica que tengas.'
                  : 'To make the most of your session, have a summary of your financial goals, your current financial statements, and any specific questions ready.'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="h6" gutterBottom align="center" sx={{ mb: 2 }}>
          {language === 'es' ? 'Elige un horario disponible' : 'Pick an available time'}
        </Typography>
        <ConsultaScheduler />
      </Container>
    </Box>
  );
};

export default ConsultantPage;
